/**
 ⚠️ Design System — Protected file
 Do not edit this file without prior approval from the Design System team.
 */

import React, { forwardRef } from 'react'
import { Insets, Platform, TextInput as RNTextInput } from 'react-native'
import styled from 'styled-components/native'

import { TextInputProps } from 'ui/components/inputs/types'
import { TextInput } from 'ui/designSystem/TextInput/TextInput'
import { Invalidate } from 'ui/svg/icons/Invalidate'
import { Search } from 'ui/svg/icons/Search'

type SearchInputBaseProps = Omit<TextInputProps, 'leftComponent' | 'rightButton'> & {
  disableClearButton?: boolean
  onClear?: () => void
}

const hitSlopInset = 10 // arbitrary hitSlop zone inset for touchable
const clearHitSlop: Insets = {
  top: hitSlopInset,
  right: hitSlopInset,
  bottom: hitSlopInset,
  left: hitSlopInset,
}

export const SearchInput = forwardRef<RNTextInput, SearchInputBaseProps>(function SearchInput(
  {
    value,
    onChangeText,
    disableClearButton,
    onClear,
    returnKeyType,
    keyboardType,
    disabled,
    ...props
  },
  ref
) {
  const hasText = typeof value === 'string' && value.length > 0
  const showClearButton = hasText && !disableClearButton && !disabled

  const handleClear = () => {
    onChangeText?.('')
    onClear?.()
  }

  const resolvedReturnKeyType: NonNullable<TextInputProps['returnKeyType']> =
    returnKeyType ?? 'search'
  const resolvedKeyboardType: TextInputProps['keyboardType'] =
    keyboardType ?? (Platform.OS === 'web' ? 'web-search' : undefined)

  return (
    <TextInput
      ref={ref}
      disabled={disabled}
      value={value}
      onChangeText={onChangeText}
      leftComponent={<StyledSearchIcon />}
      rightButton={
        showClearButton
          ? {
              icon: InvalidateIcon,
              onPress: handleClear,
              accessibilityLabel: 'Réinitialiser la recherche',
              hitSlop: clearHitSlop,
            }
          : undefined
      }
      returnKeyType={resolvedReturnKeyType}
      keyboardType={resolvedKeyboardType}
      {...props}
    />
  )
})

const StyledSearchIcon = styled(Search).attrs(({ theme }) => ({
  size: theme.designSystem.size.icon.m,
}))``

const InvalidateIcon = styled(Invalidate).attrs(({ theme }) => ({
  color: theme.designSystem.color.icon.subtle,
  size: theme.designSystem.size.icon.m,
}))``
